"use client"

import React, { useRef, useEffect, useState } from "react"

// ─── Road geometry ──────────────────────────────────────────────────────────
// 3-row snake in a 480 × 340 viewBox:
//   Row 1 (bottom) : left → right  at y = 300
//   Right turn      : curves down the right edge
//   Row 2 (middle)  : right → left  at y = 180
//   Left turn       : curves down the left edge
//   Row 3 (top)     : left → right  at y = 60
const ROAD_PATH =
  "M 60 300 L 420 300 " +
  "Q 460 300 460 260 L 460 220 Q 460 180 420 180 " +
  "L 60 180 " +
  "Q 20 180 20 140 L 20 100 Q 20 60 60 60 " +
  "L 420 60"

const VW = 480
const VH = 340

// ─── Types ───────────────────────────────────────────────────────────────────
interface ProgressMilestone {
  id: number
  label: string
  completed: boolean
  current: boolean
}

interface StopPoint {
  x: number
  y: number
}

// ─── Inline SVG checkmark ───────────────────────────────────────────────────
function SvgCheck({ cx, cy, size = 11 }: { cx: number; cy: number; size?: number }) {
  const s = size * 0.42
  return (
    <path
      d={`M ${cx - s} ${cy} L ${cx - s * 0.1} ${cy + s * 0.8} L ${cx + s} ${cy - s * 0.9}`}
      fill="none"
      stroke="white"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  )
}

// ─── Label rendered via foreignObject ───────────────────────────────────────
function StopLabel({
  cx,
  cy,
  above,
  label,
  completed,
  current,
}: {
  cx: number
  cy: number
  above: boolean
  label: string
  completed: boolean
  current: boolean
}) {
  const boxH = 36
  const stemLen = 22
  const boxY = above ? cy - stemLen - boxH : cy + stemLen

  const borderColor = completed ? "#6ee7b7" : current ? "#c7d2fe" : "#e2e8f0"
  const bgColor = completed ? "#f0fdf4" : current ? "#eef2ff" : "#f8fafc"
  const textColor = completed ? "#047857" : current ? "#4338ca" : "#94a3b8"

  return (
    <>
      {/* Dashed stem */}
      <line
        x1={cx}
        y1={above ? cy - 16 : cy + 16}
        x2={cx}
        y2={above ? cy - stemLen + 2 : cy + stemLen - 2}
        stroke={borderColor}
        strokeWidth="1.5"
        strokeDasharray="3 2"
      />
      {/* Label pill */}
      <foreignObject x={cx - 54} y={boxY} width={108} height={boxH}>
        <div
          // @ts-ignore — xmlns required inside SVG foreignObject
          xmlns="http://www.w3.org/1999/xhtml"
          style={{
            background: bgColor,
            border: `1.5px solid ${borderColor}`,
            borderRadius: 8,
            padding: "4px 6px",
            fontSize: 10,
            fontWeight: 700,
            fontFamily: "inherit",
            color: textColor,
            textAlign: "center",
            lineHeight: "1.3",
            wordBreak: "break-word",
            boxSizing: "border-box",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {label}
        </div>
      </foreignObject>
    </>
  )
}

// ─── Main component ──────────────────────────────────────────────────────────
export function CourseProgressJourney({ milestones }: { milestones: ProgressMilestone[] }) {
  const pathRef = useRef<SVGPathElement>(null)
  const [stops, setStops] = useState<StopPoint[]>([])
  const [totalLength, setTotalLength] = useState(0)

  useEffect(() => {
    const path = pathRef.current
    if (!path || !milestones.length) return
    const len = path.getTotalLength()
    setTotalLength(len)
    const pts: StopPoint[] = milestones.map((_, i) => {
      const t = milestones.length === 1 ? 0.5 : i / (milestones.length - 1)
      const pt = path.getPointAtLength(t * len)
      return { x: Math.round(pt.x * 10) / 10, y: Math.round(pt.y * 10) / 10 }
    })
    setStops(pts)
  }, [milestones])

  const completedCount = milestones.filter((m) => m.completed).length
  const currentIdx = milestones.findIndex((m) => m.current)
  const progressPercent = milestones.length
    ? Math.round((completedCount / milestones.length) * 100)
    : 0

  // Fill road up to and including the current (or last completed) stop
  const fillTargetIdx = currentIdx >= 0 ? currentIdx : completedCount - 1
  const progressFill =
    totalLength > 0 && stops.length > 0 && fillTargetIdx >= 0
      ? (fillTargetIdx / (milestones.length - 1)) * totalLength
      : 0

  return (
    <div className="w-full bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-5 pt-5 pb-2 flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-900">Progress Road</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            {completedCount} of {milestones.length} checkpoints cleared
          </p>
        </div>
        <div className="text-right">
          <span className="text-2xl font-black text-teal-600 leading-none">{progressPercent}%</span>
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">complete</p>
        </div>
      </div>

      {/* SVG Road */}
      <div className="px-2 pb-2">
        <svg
          viewBox={`-10 -10 ${VW + 20} ${VH + 20}`}
          className="w-full"
          style={{ overflow: "visible" }}
        >
          <defs>
            {/* Progress lane gradient */}
            <linearGradient id="cpj-grad" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#2dd4bf" />
              <stop offset="100%" stopColor="#818cf8" />
            </linearGradient>
            {/* Subtle glow filter for current stop */}
            <filter id="cpj-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* ── Invisible path used only for length measurement ── */}
          <path
            ref={pathRef}
            d={ROAD_PATH}
            fill="none"
            stroke="none"
            strokeWidth="0"
          />

          {/* ── Road layers ── */}
          {/* 1. Kerb / outer edge */}
          <path
            d={ROAD_PATH}
            fill="none"
            stroke="#94a3b8"
            strokeWidth="42"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* 2. Road surface */}
          <path
            d={ROAD_PATH}
            fill="none"
            stroke="#dde3ed"
            strokeWidth="38"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* 3. Completed / progress lane */}
          {totalLength > 0 && (
            <path
              d={ROAD_PATH}
              fill="none"
              stroke="url(#cpj-grad)"
              strokeWidth="38"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={`${progressFill} ${totalLength}`}
              style={{ transition: "stroke-dasharray 0.9s ease-in-out" }}
            />
          )}
          {/* 4. White inner kerb lines */}
          <path
            d={ROAD_PATH}
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={0.55}
          />
          {/* 5. Centre dashes */}
          <path
            d={ROAD_PATH}
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="16 11"
            opacity={0.75}
          />

          {/* ── START flag (first stop) ── */}
          {stops.length > 0 && (
            <g>
              {/* Flag pole */}
              <line
                x1={stops[0].x}
                y1={stops[0].y - 16}
                x2={stops[0].x}
                y2={stops[0].y - 42}
                stroke="#f59e0b"
                strokeWidth="2"
                strokeLinecap="round"
              />
              {/* Flag banner */}
              <polygon
                points={`${stops[0].x},${stops[0].y - 42} ${stops[0].x + 22},${stops[0].y - 36} ${stops[0].x},${stops[0].y - 30}`}
                fill="#f59e0b"
              />
              <text
                x={stops[0].x + 7}
                y={stops[0].y - 34}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="7"
                fontWeight="800"
                fill="white"
                letterSpacing="0.5"
              >
                GO
              </text>
            </g>
          )}

          {/* ── FINISH checker flag (last stop) ── */}
          {stops.length > 1 && (() => {
            const last = stops[stops.length - 1]
            const flagTop = last.y - 48
            const sq = 7
            const cols = 4
            const rows = 3
            return (
              <g>
                <line
                  x1={last.x}
                  y1={last.y - 16}
                  x2={last.x}
                  y2={flagTop + rows * sq}
                  stroke="#475569"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                {Array.from({ length: rows }).map((_, r) =>
                  Array.from({ length: cols }).map((_, c) => (
                    <rect
                      key={`${r}-${c}`}
                      x={last.x + c * sq}
                      y={flagTop + r * sq}
                      width={sq}
                      height={sq}
                      fill={(r + c) % 2 === 0 ? "#1e293b" : "white"}
                    />
                  ))
                )}
                <rect
                  x={last.x}
                  y={flagTop}
                  width={cols * sq}
                  height={rows * sq}
                  fill="none"
                  stroke="#475569"
                  strokeWidth="1"
                />
              </g>
            )
          })()}

          {/* ── Milestone stops ── */}
          {stops.map((stop, i) => {
            const milestone = milestones[i]
            if (!milestone) return null
            const { completed, current, label } = milestone
            const isPending = !completed && !current

            // Labels go above on the lower half of the svg, below on the upper half
            const labelAbove = stop.y > VH / 2

            const r = current ? 17 : 14
            const circleFill = completed ? "#14b8a6" : current ? "#6366f1" : "#f8fafc"
            const circleStroke = completed ? "#0d9488" : current ? "#4338ca" : "#cbd5e1"

            return (
              <g key={milestone.id}>
                {/* Label + stem */}
                <StopLabel
                  cx={stop.x}
                  cy={stop.y}
                  above={labelAbove}
                  label={label}
                  completed={completed}
                  current={current}
                />

                {/* Animated pulse ring for active stop */}
                {current && (
                  <circle cx={stop.x} cy={stop.y} r={r} fill="none" stroke="#818cf8" strokeWidth="2" opacity={0.4}>
                    <animate attributeName="r" from={r} to={r + 12} dur="1.6s" repeatCount="indefinite" />
                    <animate attributeName="opacity" from="0.45" to="0" dur="1.6s" repeatCount="indefinite" />
                  </circle>
                )}

                {/* Main circle */}
                <circle
                  cx={stop.x}
                  cy={stop.y}
                  r={r}
                  fill={circleFill}
                  stroke={circleStroke}
                  strokeWidth="3"
                  filter={current ? "url(#cpj-glow)" : undefined}
                />

                {/* Icon inside circle */}
                {completed ? (
                  <SvgCheck cx={stop.x} cy={stop.y} size={12} />
                ) : current ? (
                  <>
                    <circle cx={stop.x} cy={stop.y} r={6} fill="#818cf8" />
                    <circle cx={stop.x} cy={stop.y} r={2.5} fill="white" />
                  </>
                ) : (
                  <text
                    x={stop.x}
                    y={stop.y + 1}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="10"
                    fontWeight="bold"
                    fill="#94a3b8"
                  >
                    {i + 1}
                  </text>
                )}
              </g>
            )
          })}
        </svg>
      </div>

      {/* Legend */}
      <div className="border-t border-slate-100 px-5 py-3 flex items-center gap-5 text-[11px] text-slate-500 font-semibold">
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-teal-400 inline-block shrink-0" />
          Done
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-indigo-400 inline-block shrink-0" />
          Active
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-slate-200 inline-block shrink-0" />
          Ahead
        </span>
      </div>
    </div>
  )
}
